import React, { createContext, FC, memo, ReactNode, useContext, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { ITimeCategoryBaseDTO, ITimesheetDTO, ITimesheetSummaryDTO } from 'src/api/dtos';
import CommonTimesheetRepository from 'src/api/repositories/common/v1/timesheet.repository';
import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { IError } from 'src/api/types';
import { PermissionsEnums } from 'src/shared/enums';
import { usePermissions } from 'src/shared/hooks';
import { getErrorMessage } from 'src/shared/utils';

import { useTimesheetQuery } from './TimesheetQueryProvider';

const INVALID_TIMESHEET_EMPLOYEE_VALIDATION_MESSAGE = 'Invalid timesheet employee.';

type TimesheetDataProps = {
  timesheet: ITimesheetDTO | undefined;
  timesheetSummary: ITimesheetSummaryDTO | undefined;
  timeCategories: ITimeCategoryBaseDTO[];
  loadingTimesheet: boolean;
  loadingTimesheetSummary: boolean;
  loadingTimeCategories: boolean;
  isInvalid: boolean;
  isSubmitEnabled: boolean;
  onRefetch: () => void;
};

const TimesheetDataContext = createContext<TimesheetDataProps>({} as TimesheetDataProps);

export const useTimesheetData = (): TimesheetDataProps => useContext<TimesheetDataProps>(TimesheetDataContext);

export const TimesheetDataProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const hasDepartmentAllocation = usePermissions(PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION);
  const hasJobCosting = usePermissions(PermissionsEnums.FEATUREJOB_COSTING_FUNCTION);
  const hasExpense = usePermissions(PermissionsEnums.WTS_INPUT_EXPENSE);

  const { selectedPayPeriodWeek, type, showDepartment, showJobs, showExpenses } = useTimesheetQuery();

  const {
    data: timesheet,
    isLoading: isLoadingTimesheet,
    isFetching: isFetchingTimesheet,
    refetch: refetchTimesheet,
    error: timesheetError,
  } = TimesheetRepository.endpoints.useGetTimesheetGridQuery(
    selectedPayPeriodWeek
      ? {
          startDate: selectedPayPeriodWeek.startDate,
          endDate: selectedPayPeriodWeek.endDate,
        }
      : skipToken,
  );

  const loadingTimesheet = isLoadingTimesheet || isFetchingTimesheet;

  const isInvalid = useMemo(
    () =>
      !!timesheetError &&
      getErrorMessage(timesheetError as IError, '') === INVALID_TIMESHEET_EMPLOYEE_VALIDATION_MESSAGE,
    [timesheetError],
  );

  const isSubmitEnabled = useMemo(() => !!timesheet && timesheet.canSubmitToSupervisor, [timesheet]);

  const {
    data: timesheetSummary,
    isLoading: isLoadingTimesheetSummary,
    isFetching: isFetchingTimesheetSummary,
    refetch: refetchTimesheetSummary,
  } = TimesheetRepository.endpoints.useGetTimesheetGridSummaryQuery(
    selectedPayPeriodWeek
      ? {
          startDate: selectedPayPeriodWeek.startDate,
          endDate: selectedPayPeriodWeek.endDate,
          summaryType: type,
          breakdownWithDepartments: hasDepartmentAllocation && showDepartment,
          breakdownWithJobs: hasJobCosting && showJobs,
          breakdownWithExpenses: hasExpense && showExpenses,
        }
      : skipToken,
  );

  const loadingTimesheetSummary = isLoadingTimesheetSummary || isFetchingTimesheetSummary;

  const handleRefetch = () => {
    refetchTimesheet();
    refetchTimesheetSummary();
  };

  const {
    data: timeCategories,
    isLoading: isLoadingTimeCategories,
    isFetching: isFetchingTimeCategories,
  } = CommonTimesheetRepository.endpoints.useGetTimeCategoriesQuery(
    { isMemo: true },
    {
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loadingTimeCategories = isLoadingTimeCategories || isFetchingTimeCategories;

  return (
    <TimesheetDataContext.Provider
      value={{
        timesheet,
        timesheetSummary,
        timeCategories,
        loadingTimesheet,
        loadingTimesheetSummary,
        loadingTimeCategories,
        isInvalid,
        isSubmitEnabled,
        onRefetch: handleRefetch,
      }}
    >
      {children}
    </TimesheetDataContext.Provider>
  );
});
